import React from 'react';
import {
  Button,
  Card,
  CardHeader,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useFormik } from 'formik';
import { useMutation, useParameterizedQuery } from 'react-fetching-library';
import { DataGridPro } from '@mui/x-data-grid-pro';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import toast from 'react-hot-toast';

import { piggyBankEntryColumns } from '../../../../../services/column-definitions';
import { getPiggyBankAction, getPiggyBankEntriesAction, submitPiggyBankEntryAction } from '../../../../../services/request';
import { extractApiErrorMessage } from '../../../../../services/functions';

export const PiggyBankSection = ({ purchaserId }) => {
  const { query: queryPiggyBank, payload: piggyBankPayload } = useParameterizedQuery(getPiggyBankAction);
  const { query: queryEntries, payload: entriesPayload, loading: entriesLoading } = useParameterizedQuery(getPiggyBankEntriesAction);
  const { mutate, loading: submitting } = useMutation(submitPiggyBankEntryAction);
  const [modalOpen, setModalOpen] = React.useState(false);

  React.useEffect(() => {
    if (purchaserId) {
      queryPiggyBank(purchaserId);
      queryEntries(purchaserId, 'GET');
    }
  }, [purchaserId]);

  const balance = piggyBankPayload?.results?.[0]?.balance ?? 0;
  const entries = entriesPayload?.results ?? [];

  const getDefaultExpiry = () => {
    const d = new Date();
    d.setDate(d.getDate() + 730);

    return d.toISOString().split('T')[0];
  };

  const formik = useFormik({
    initialValues: { amount: 0, description: '', expires_at: getDefaultExpiry() },
    validate: values => {
      const errors = {};
      if (!values.amount || Number(values.amount) === 0) {
        errors.amount = 'Wartość musi być różna od 0';
      }

      return errors;
    },
    onSubmit: async values => {
      const submitValues = {
        ...values,
        purchaser: purchaserId,
        expires_at: Number(values.amount) > 0 ? values.expires_at : null
      };
      const { error, payload } = await mutate(submitValues, 'POST');
      if (error) {
        toast.error(extractApiErrorMessage(payload));

        return;
      }
      toast.success('Dodano wpis do skarbonki');
      setModalOpen(false);
      formik.resetForm({ values: { amount: 0, description: '', expires_at: getDefaultExpiry() } });
      queryPiggyBank(purchaserId);
      queryEntries(purchaserId, 'GET');
    }
  });

  if (!purchaserId) {
    return null;
  }

  return (
    <Card sx={{ mt: 3 }}>
      <CardHeader
        title="Skarbonka"
        action={
          submitting ? (
            <CircularProgress size={24} />
          ) : (
            <IconButton color="primary" onClick={() => setModalOpen(true)}>
              <AddCircleIcon /> Korekta
            </IconButton>
          )
        }
      />
      <Typography sx={{ m: 2 }}>
        Aktualny stan skarbonki: <b>{balance} zł</b>
      </Typography>
      <DataGridPro
        autoHeight
        density="compact"
        loading={entriesLoading}
        rows={entries}
        columns={piggyBankEntryColumns()}
      />
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth sx={{ zIndex: 9999999 }}>
        <DialogTitle>Dodaj korektę ręczną do skarbonki</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={2}>
            <TextField
              name="amount"
              label="Kwota (zł, może być ujemna)"
              type="number"
              fullWidth
              value={formik.values.amount}
              onChange={formik.handleChange}
              error={Boolean(formik.errors.amount)}
              helperText={formik.errors.amount}
            />
            <TextField
              name="description"
              label="Opis"
              multiline
              rows={3}
              fullWidth
              value={formik.values.description}
              onChange={formik.handleChange}
            />
            {Number(formik.values.amount) > 0 && (
              <>
                <TextField
                  name="expires_at"
                  label="Data wygaśnięcia"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  disabled={formik.values.expires_at === null}
                  value={formik.values.expires_at || ''}
                  onChange={formik.handleChange}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.expires_at === null}
                      onChange={e => formik.setFieldValue('expires_at', e.target.checked ? null : getDefaultExpiry())}
                    />
                  }
                  label="Bez daty wygaśnięcia"
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={formik.handleSubmit} variant="contained" color="primary">
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};
